<template>
    <div class="modal" 
        tabindex="-1" 
        role="dialog" 
        id="muven-order-flow-modal" 
        :class="{
            'order-pending': order.isPending(),
            'order-canceled': order.isCanceled(),
        }">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <div class="modal-header" v-show="loading">
                    <h5 class="modal-title">Aguarde, carregando dados do pedido</h5>
                </div>
                <div class="modal-header" v-show="!loading">
                    <h5 class="modal-title">Andamento do pedido</h5>
                    <p class="modal-subtitle">Status: 
                        <span :class="{
                            'text-muted': order.isPending(),
                            'text-success': !order.isPending() && !order.isCanceled(),
                            'text-danger': order.isCanceled(),
                        }">{{order.getOrderStatusEnumDescription()}}</span>
                    </p>
                    <p class="modal-subtitle">Feito em: {{order.createdAt | dateTimeFilter}}</p>
                    <p class="modal-subtitle">Código: {{order.idExternalChannel}}</p>
                </div>
                <div class="modal-body" v-show="loading">
                    <Loading />
                </div>  
                <div class="modal-body" v-show="!loading">
                    <div>
                        <div class="row order-items-container">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Produto</th>
                                        <th class="text-right">Preço</th>
                                        <th class="text-right">Qtd</th>
                                        <th class="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in order.items" :key="index" class="order-item">
                                        <td>
                                            <p class="product-name">{{item.product.name}}</p>
                                            <p class="product-sku">SKU {{item.product.skuCode}}</p>
                                        </td>
                                        <td class="text-right">{{item.price | currency}}</td>
                                        <td class="text-right">{{item.amount}}</td>
                                        <td class="text-right">{{item.total | currency}}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="row order-additional-info">                           
                            <div class="col-12 col-sm-4 address-container">
                                <label>Endereço de entrega</label>
                                <p class="mb-0"><span>{{order.getShippingAddress().street}}</span><span>, {{order.getShippingAddress().number}}</span></p>
                                <p class="mb-0">{{order.getShippingAddress().neighborhood}}</p>
                                <p class="mb-0">{{order.getShippingAddress().complement}}</p>
                                <p class="mb-0"><span>{{order.getShippingAddress().city}}</span>/<span>{{order.getShippingAddress().state}}</span></p>
                                <p class="mb-0">{{order.getShippingAddress().reference}}</p>
                            </div>
                            
                            <div class="col-12 col-sm-4 customer-container">
                                <label>Cliente</label>
                                <p class="mb-0">{{order.customer.fullName() | infoOrPlaceholder}}</p>
                                <p class="mb-0">{{order.customer.email | infoOrPlaceholder}}</p>
                                <p class="mb-0">{{order.customer.phone | infoOrPlaceholder}}</p>
                            </div>

                            <div class="col-12 col-sm-4 totals-container">
                                <label>Totais</label>

                                <div class="value-container">
                                    <label>Produtos</label>
                                    <p class="mb-0">{{order.getItemsTotal() | currency}}</p>
                                </div>
                                <div class="value-container">
                                    <label>Entrega</label>
                                    <p class="mb-0">{{order.getInvoice().freightTotal | currency}}</p>
                                </div>
                                <div class="value-container" :class="{'has-change-value': order.getPayment().hasChangeValue()}">
                                    <label>Troco</label>
                                    <p class="mb-0">{{order.getPayment().changeValue | currency}}</p>
                                </div>
                                <div class="value-container total">
                                    <p class="mb-0">{{order.total | currency}}</p>
                                </div>
                            </div>
                        </div>

                        <div class="status-list">
                            <div class="status-item">
                                <div class="status-icon-container">
                                    <fa icon="check-circle" class="status-icon done" />
                                </div>
                                <div class="status-description-container">
                                    <p class="status-description">{{statusApproved.description}}</p>
                                </div>

                                <div class="status-line" :class="{'status-line-done': statusPosted.done}"></div>
                            </div> 

                            <div class="status-item">
                                <div class="status-icon-container">
                                    <fa v-show="statusPosted.done" icon="truck-moving" class="status-icon done" />
                                    <fa v-show="!statusPosted.done" icon="circle" class="status-icon"/>
                                </div>
                                <div class="status-description-container">
                                    <p class="status-description" v-show="!statusPosted.done">{{statusPosted.description}}</p>
                                    <p class="status-description" v-show="statusPosted.done">{{statusPosted.descriptionDone}}</p>
                                </div>

                                <div class="status-line" :class="{'status-line-done': statusDelivered.done}"></div>
                            </div> 

                            <div class="status-item">
                                <div class="status-icon-container">
                                    <fa v-show="statusDelivered.done" icon="handshake" class="status-icon done" />
                                    <fa v-show="!statusDelivered.done" icon="circle" class="status-icon"/>
                                </div>
                                <div class="status-description-container">
                                    <p class="status-description" v-show="!statusDelivered.done">{{statusDelivered.description}}</p>
                                    <p class="status-description" v-show="statusDelivered.done">{{statusDelivered.descriptionDone}}</p>
                                </div>
                            </div>                             
                        </div>
                    </div>
                </div>
                <div class="modal-footer order-flow-actions" v-show="!loading">
                    <button type="button" 
                        class="btn btn-outline-danger dangerous-button" 
                        :disabled="loadingCanceled"
                        v-show="order.isPending() || order.isApproved() || order.isPosted()"
                        @click="setOrderStatusToCanceled(order)">
                            <span v-show="order.isPending()">Rejeitar</span>
                            <span v-show="!order.isPending()">Cancelar pedido</span>
                            <fa icon="exclamation-triangle" class="btn-icon" v-show="!loadingCanceled"/>
                            <Loading v-show="loadingCanceled"/>
                    </button>

                    <span v-show="order.isPending()">
                        <button type="button" 
                            class="btn btn-primary" 
                            :disabled="loadingApproved"
                            @click="setOrderStatusToApproved(order)">
                            Aprovar 

                            <Loading v-show="loadingApproved" />
                        </button>
                    </span>

                    <span v-show="order.isApproved()">
                        <button type="button" 
                            class="btn btn-primary" 
                            :disabled="loadingPosted"
                            @click="setOrderStatusToPosted(order)">
                            Liberar para entrega

                            <Loading v-show="loadingPosted"/>
                        </button>
                    </span>

                    <span v-show="order.isPosted()">
                        <button type="button" 
                            class="btn btn-primary" 
                            :disabled="loadingDelivered"
                            @click="setOrderStatusToDelivered(order)">
                            Finalizar pedido

                            <Loading v-show="loadingDelivered"/>
                        </button>
                    </span>

                    <button type="button" 
                        class="btn btn-outline-secondary float-right" 
                        @click="closeModal()">Fechar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import modalService from "@/utils/services/ModalService";
import orderService from "@/order/services/OrderService";
import { OrderModel } from '@/order/models/OrderModel';
import {dateTimeFilter} from '@/utils/filters/DateTimeFilter'
import {infoOrPlaceholder} from "@/utils/filters/InformationOrPlaceholderFilter"
import { OrderStatusEnum } from '@c2b/muven-commons';

const TIME_TO_CLOSE_MODAL = 1000;
const ID_MODAL = "#muven-order-flow-modal";

export default {
    name: 'OrderFlowCmp',
    data() {
        return {
            loading: false,
            loadingApproved: false,
            loadingPosted: false,
            loadingDelivered: false,
            loadingCanceled: false,
            order: new OrderModel(),
            statusApproved: {
                description: "Pedido aprovado",
                date: new Date(),
                done: true,
            },
            statusPosted: {
                description: "Liberado para entrega",
                descriptionDone: "Saiu para entrega",
                date: new Date(),
                done: false,
            },
            statusDelivered: {
                description: "Entregue ao destinatário",
                descriptionDone: "Encerrado",
                date: new Date(),
                done: false,
            },
        }
    },
    filters: {
        dateTimeFilter,
        infoOrPlaceholder,
    },
    methods: {
        async openModal(orderId) {
            this.loading = true;

            try {
                await modalService.show(ID_MODAL);
                this.order = await orderService.getOrderById(orderId);
                this._parseSteps();
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loading = false;
        },
        async closeModal() {
            await modalService.hide(ID_MODAL);
        },
        async setOrderStatusToApproved(order) {
            this.loadingApproved = true;
            await this._changeStatus(order, OrderStatusEnum.Approved);
            this.loadingApproved = false;
        },
        async setOrderStatusToPosted(order) {
            this.loadingPosted = true;
            await this._changeStatus(order, OrderStatusEnum.Posted);
            this.loadingPosted = false;
        },
        async setOrderStatusToDelivered(order) {
            this.loadingDelivered = true;
            await this._changeStatus(order, OrderStatusEnum.Delivered);
            this.loadingDelivered = false;
        },
        async setOrderStatusToCanceled(order) {
            this.loadingCanceled = true;
            await this._changeStatus(order, OrderStatusEnum.Canceled);
            this.loadingCanceled = false;
        },
        async _changeStatus(order, newStatus) {
            let modOrder = null;

            try {
                switch (newStatus) {
                    case OrderStatusEnum.Approved:
                        modOrder = await orderService.setStatusToApproved(order.id)
                        break;
                    case OrderStatusEnum.Posted:
                        modOrder = await orderService.setStatusToPosted(order.id)
                        break;
                    case OrderStatusEnum.Delivered:
                        modOrder = await orderService.setStatusToDelivered(order.id)
                        break;
                    case OrderStatusEnum.Canceled:
                        modOrder = await orderService.setStatusToCanceled(order.id)
                        break;                    
                }
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                });
            }
            
            this.order.setStatus(modOrder.status);
            this.$emit('status-changed', {
                idExternalChannel: modOrder.idExternalChannel, 
                status: modOrder.status,
            });
            this._parseSteps();
            this._closeModalAfterStatusChange();

            return modOrder;
        },
        _closeModalAfterStatusChange() {
            setTimeout(async () => {
                await this.closeModal()
            }, TIME_TO_CLOSE_MODAL);
        },
        _parseSteps() {
            if (!this.order.isPending() && !this.order.isCanceled()) {
                this.statusApproved.done = true;
            } else {
                this.statusApproved.done = false;
            }

            if (this.order.isPosted() || this.order.isDelivered() || this.order.isFinalized()) {
                this.statusPosted.done = true;
            } else {
                this.statusPosted.done = false;
            }

            if (this.order.isDelivered() || this.order.isFinalized()) {
                this.statusDelivered.done = true;
            } else {
                this.statusDelivered.done = false;
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../utils/styles/constants.scss";

.modal {
    &.order-pending,
    &.order-canceled {
        .modal-body {
            .order-additional-info {
                margin-bottom: -17px;
            }

            .status-list {
                display: none;
            }
        }
    }

    .modal-body {
        .order-items-container,
        .order-additional-info {
            background-color: var(--surface-2);
        }

        .order-items-container {
            margin-top: -16px;
            border-bottom: 1px dashed var(--border);
            max-height: 215px;
            overflow-y: auto;

            .table {
                background-color: var(--surface-2);
                border: none;

                thead tr th,
                tbody tr td {
                    border: none;
                }

                .order-item {
                    .product-name,
                    .product-sku {
                        margin-bottom: 0;
                    }

                    .product-sku {
                        color: var(--text-muted);
                        font-size: .9em;
                    }
                }
            }            
        }

         .order-additional-info {
             padding-top: 15px;
             padding-bottom: 15px;
             margin-bottom: 15px;
             border-bottom: 1px solid var(--border);
             word-wrap: break-word;

             .customer-container {

             }

             .address-container {

             }

             .totals-container {
                 .value-container {
                     clear: both;

                     label {
                         font-weight: normal;
                         color: var(--text);
                         float: left;                  
                         text-transform: none;       
                         margin-bottom: 0;
                     }

                     p {
                         float: right;
                         text-align: right;
                         margin-bottom: 0;
                     }

                     &.has-change-value {
                         p {
                             font-weight: 600;
                             color: var(--success-fg);
                         }
                     }

                     &.total {
                         p {
                            font-weight: 600;
                            font-size: 1.5em;
                            color: $color-primary;
                         }
                     }
                 }
             }
         }

        .status-list {
            position: relative;
            display: flex;
            justify-content: space-around;                        

            .status-item {
                position: relative;
                width: 33.33%;
                text-align: center;
                padding: 8px;

                .status-line {
                    position: absolute;
                    height: 2px;
                    background-color: var(--border);
                    top: 29px;
                    z-index: 1;
                    width: 0;
                    z-index: 1;
                    left: 145px;           
                    transition: width .3s ease;

                    &.status-line-done {
                        width: 235px;
                        background-color: var(--success-solid);
                    }
                }

                .status-icon-container {
                    position: relative;

                    .status-icon {
                        position: absolute;
                        left: 50%;
                        transform: translateX(-50%);
                        top: 14px;
                        color: var(--text-muted);
                        z-index: 3;
                        background-color: var(--surface-1);
                        width: 40px;

                        &.done {
                            width: 50px;
                            top: 7px;
                            padding-top: 0;
                            font-size: 2em;
                            color: var(--success-fg);
                        }
                    }
                }

                .status-description-container {
                    margin-top: 55px;

                    .status-description {
                        margin-top: 16px;
                        margin-bottom: 0;
                    }
                }

                .status-date-container {
                    .status-date {
                        margin-top: 0;
                        margin-bottom: 0;
                        color: var(--text-muted);
                    }   
                }

                .status-action-container {
                    margin-top: 15px;
                    text-align: center;

                    .btn {
                        &:last-of-type {
                            margin-left: 8px;
                        }
                    }
                }
            }
        }
    }

    .modal-footer {
        &.order-flow-actions {
            position: relative;
            
            .dangerous-button {
                position: absolute;
                left: 15px;

                .btn-icon {
                    margin-left: 8px;
                }
            }

            .btn {
                float: right;
            }
        }
    }
}
</style>